import {
    AddOutline,
    Box,
    Button,
    Delete,
    Divider,
    IconButton,
    QueryBuilder,
    QueryCondition,
    QueryConditionGroup,
    Select,
    TextInput,
} from "@bcc-ui/ui-components";
import { FormikErrors, FormikTouched, getIn } from "formik";
import React from "react";
import { useTranslation } from "react-i18next";

import { AssetCustomViewFormValues } from "./custom-views/ManageAssetCustomViewForm";
import Chevron from "components/icons/Chevron";
import style from "components/reports/advanced-search/group-condition/group-condition.scss";
import { FilterLogicOperator, FilterOperator } from "domain/asset-management/assetFilterConstants";
import { ALL_COLUMNS } from "domain/asset-management/assetPathMapping";
import {
    AssetCustomViewCondition,
    AssetCustomViewConditionGroup,
    AssetCustomViewFilterEntry,
    AssetCustomViewOperator,
    LogicOperator,
} from "domain/asset-management/assets";
import { resolveColor } from "utils/commonFunctions";
import { useTheme } from "utils/useTheme";

import testIds from "testIds.json";

export const isConditionGroup = (entry: AssetCustomViewFilterEntry): entry is AssetCustomViewConditionGroup =>
    "conditions" in entry;

export const getFilterError = (
    fieldName: string,
    absoluteIndex: number,
    touched: FormikTouched<AssetCustomViewFilterEntry>[] | undefined,
    filterErrors: string | string[] | FormikErrors<AssetCustomViewFilterEntry>[] | undefined,
    conditionIndex?: number
): string | undefined => {
    const path = `[${absoluteIndex}]${conditionIndex !== undefined ? `.conditions[${conditionIndex}]` : ``}.${fieldName}`;
    return getIn(touched, path) && getIn(filterErrors, path);
};

const EMPTY_CONDITION: AssetCustomViewCondition = {
    field: "",
    operator: FilterOperator.EQUAL,
    value: "",
    logicOperator: FilterLogicOperator.AND,
};

const FIELD_ITEMS = ALL_COLUMNS.map((column) => ({ value: column.value, label: column.label }));

const RISK_LEVEL_ITEMS = [
    { value: "LOW", label: "LOW" },
    { value: "MEDIUM", label: "MEDIUM" },
    { value: "HIGH", label: "HIGH" },
];

interface Props {
    filters: AssetCustomViewFilterEntry[];
    onChange: (filters: AssetCustomViewFilterEntry[]) => void;
    dirty: boolean;
    touched: FormikTouched<AssetCustomViewFilterEntry>[] | undefined;
    errors: FormikErrors<AssetCustomViewFormValues>;
}

export const AssetViewFilters = (props: Props) => {
    const { t } = useTranslation();
    const theme = useTheme();

    const logicalOperatorTexts = {
        and: t("Common.and"),
        or: t("Common.or"),
        where: t("AdvancedSearch.where"),
    };

    const topLevelConditions = props.filters.filter(
        (entry): entry is AssetCustomViewCondition => !isConditionGroup(entry)
    );
    const groups = props.filters.filter((entry): entry is AssetCustomViewConditionGroup => isConditionGroup(entry));

    const addCondition = () => {
        props.onChange([...props.filters, { ...EMPTY_CONDITION }]);
    };

    const addGroup = () => {
        const newGroup: AssetCustomViewConditionGroup = {
            conditions: [{ ...EMPTY_CONDITION }, { ...EMPTY_CONDITION }],
            logicOperator: "AND",
            entryLogicOperator: FilterLogicOperator.AND,
        };
        props.onChange([...props.filters, newGroup]);
    };

    const removeEntry = (index: number) => {
        props.onChange(props.filters.filter((_, entryIndex) => entryIndex !== index));
    };

    const updateCondition = (index: number, updated: Partial<AssetCustomViewCondition>) => {
        props.onChange(
            props.filters.map((entry, entryIndex) => {
                if (entryIndex !== index || isConditionGroup(entry)) {
                    return entry;
                }
                return { ...entry, ...updated };
            })
        );
    };

    const removeGroupCondition = (groupIndex: number, conditionIndex: number) => {
        const group = props.filters[groupIndex];
        if (!isConditionGroup(group)) {
            return;
        }
        const updatedConditions = group.conditions.filter((_, filterIndex) => filterIndex !== conditionIndex);
        if (updatedConditions.length === 0) {
            removeEntry(groupIndex);
            return;
        }
        props.onChange(
            props.filters.map((entry, index) =>
                index === groupIndex ? { ...group, conditions: updatedConditions } : entry
            )
        );
    };

    const updateGroupCondition = (
        groupIndex: number,
        conditionIndex: number,
        updated: Partial<AssetCustomViewCondition>
    ) => {
        const group = props.filters[groupIndex];
        if (!isConditionGroup(group)) {
            return;
        }
        const updatedConditions = group.conditions.map((condition, index) =>
            index === conditionIndex ? { ...condition, ...updated } : condition
        );
        props.onChange(
            props.filters.map((entry, entryIndex) =>
                entryIndex === groupIndex ? { ...group, conditions: updatedConditions } : entry
            )
        );
    };

    const addConditionToGroup = (groupIndex: number) => {
        const group = props.filters[groupIndex];
        if (!isConditionGroup(group)) {
            return;
        }
        props.onChange(
            props.filters.map((entry, index) =>
                index === groupIndex ? { ...group, conditions: [...group.conditions, { ...EMPTY_CONDITION }] } : entry
            )
        );
    };

    const updateGroupEntryLogic = (groupIndex: number) => {
        const group = props.filters[groupIndex];
        if (!isConditionGroup(group)) {
            return;
        }
        const newOperator: LogicOperator = group.entryLogicOperator === "AND" ? "OR" : "AND";

        props.onChange(
            props.filters.map((entry, index) =>
                index === groupIndex ? { ...group, entryLogicOperator: newOperator } : entry
            )
        );
    };

    const operatorItems = [
        { value: FilterOperator.EQUAL, label: t("AssetManagement.filters.operators.equals") },
        { value: FilterOperator.NOT_EQUAL, label: t("AssetManagement.filters.operators.notEquals") },
        { value: FilterOperator.MATCH, label: t("AssetManagement.filters.operators.match") },
        { value: FilterOperator.EXIST, label: t("AssetManagement.filters.operators.exists") },
    ];

    const renderConditionInputs = (
        condition: AssetCustomViewCondition,
        absoluteIndex: number,
        onUpdate: (updated: Partial<AssetCustomViewCondition>) => void,
        conditionIndex?: number
    ) => (
        <>
            <Select
                name="field"
                placeholder={t("AssetManagement.filters.selectField")}
                items={FIELD_ITEMS}
                selectedByDefault={condition.field}
                onSelection={(value) => onUpdate({ field: String(value) })}
                error={
                    props.dirty
                        ? getFilterError("field", absoluteIndex, props.touched, props.errors.filters, conditionIndex)
                        : undefined
                }
                dropdownMaxHeight={200}
                dropdownPosition="bottom-start"
                hideLabelSection
                closeOnSelection
            />
            <Select
                name="operator"
                placeholder={t("AssetManagement.filters.operators.equals")}
                items={operatorItems}
                selectedByDefault={condition.operator}
                onSelection={(value) => onUpdate({ operator: value as AssetCustomViewOperator })}
                hideLabelSection
                closeOnSelection
            />
            {condition.operator !== FilterOperator.EXIST &&
                (condition.field === "risk_level" ? (
                    <Select
                        name="value"
                        placeholder={t("AssetManagement.filters.selectField")}
                        items={RISK_LEVEL_ITEMS}
                        selectedByDefault={condition.value}
                        onSelection={(value) => onUpdate({ value: String(value) })}
                        error={
                            props.dirty
                                ? getFilterError(
                                      "value",
                                      absoluteIndex,
                                      props.touched,
                                      props.errors.filters,
                                      conditionIndex
                                  )
                                : undefined
                        }
                        hideLabelSection
                        closeOnSelection
                        className={style.filterValueSelect}
                    />
                ) : (
                    <TextInput
                        name="value"
                        value={condition.value}
                        onChange={(event) => onUpdate({ value: event.target.value })}
                        error={
                            props.dirty
                                ? getFilterError(
                                      "value",
                                      absoluteIndex,
                                      props.touched,
                                      props.errors.filters,
                                      conditionIndex
                                  )
                                : undefined
                        }
                        hideLabelSection
                    />
                ))}
        </>
    );

    const footerButtons = (
        <Box columnLayout gap={"Xs"}>
            {props.filters.length === 0 && (
                <Box paddingBottom={"Xs"}>
                    <div className={style.assetFilterEmptyText}>{t("AssetManagement.filters.noFiltersYet")}</div>
                </Box>
            )}
            <Box paddingBottom={"Xs"} justifyContent={"SpaceBetween"} className={style.assetFilterWidth}>
                <Box gap={"Sm"}>
                    <Button
                        variant="Tertiary"
                        type="button"
                        icon={<AddOutline color={resolveColor("--theme-active-primary")} />}
                        onClick={addCondition}
                    >
                        {t("AssetManagement.filters.addCondition")}
                    </Button>
                    <Divider direction={"Vertical"} />
                    <Button
                        variant="Tertiary"
                        type="button"
                        icon={<AddOutline color={resolveColor("--theme-active-primary")} />}
                        onClick={addGroup}
                    >
                        {t("AssetManagement.filters.addGroup")}
                    </Button>
                </Box>
            </Box>
        </Box>
    );

    return (
        <QueryBuilder footerButtons={footerButtons}>
            {props.filters.map((entry, absoluteIndex) => {
                if (!isConditionGroup(entry)) {
                    return (
                        <QueryConditionGroup
                            key={absoluteIndex}
                            conditions={[
                                [
                                    <QueryCondition
                                        key={absoluteIndex}
                                        index={absoluteIndex}
                                        logicalOperator={
                                            absoluteIndex === 0
                                                ? "WHERE"
                                                : (topLevelConditions[1]?.logicOperator ?? FilterLogicOperator.AND)
                                        }
                                        logicalOperatorTexts={logicalOperatorTexts}
                                        disableLogicalOperatorToggle={absoluteIndex > 1}
                                        handleLogicalOperatorChange={(operator) => {
                                            const newOperator =
                                                operator === "WHERE"
                                                    ? FilterLogicOperator.AND
                                                    : (operator as FilterLogicOperator);
                                            props.onChange(
                                                props.filters.map((entry) =>
                                                    isConditionGroup(entry)
                                                        ? entry
                                                        : { ...entry, logicOperator: newOperator }
                                                )
                                            );
                                        }}
                                        handleDelete={removeEntry}
                                    >
                                        {renderConditionInputs(entry, absoluteIndex, (updated) =>
                                            updateCondition(absoluteIndex, updated)
                                        )}
                                    </QueryCondition>,
                                ],
                            ]}
                            firstGroup={absoluteIndex === 0}
                            depth={0}
                            logicalOperator="AND"
                            groupHeadingText=""
                            headerButtons={null}
                        />
                    );
                }

                const groupRelativeIndex = groups.indexOf(entry);
                const showConnector = absoluteIndex > 0;
                const groupConditionNodes = entry.conditions.map((condition, conditionIndex) => {
                    return [
                        <QueryCondition
                            key={conditionIndex}
                            index={conditionIndex}
                            logicalOperator={
                                conditionIndex === 0
                                    ? "WHERE"
                                    : (entry.conditions[1]?.logicOperator ?? FilterLogicOperator.AND)
                            }
                            logicalOperatorTexts={logicalOperatorTexts}
                            disableLogicalOperatorToggle={conditionIndex > 1}
                            handleLogicalOperatorChange={(operator) => {
                                const newOperator =
                                    operator === "WHERE" ? FilterLogicOperator.AND : (operator as FilterLogicOperator);
                                props.onChange(
                                    props.filters.map((entry, index) =>
                                        index !== absoluteIndex || !isConditionGroup(entry)
                                            ? entry
                                            : {
                                                  ...entry,
                                                  conditions: entry.conditions.map((condition) => ({
                                                      ...condition,
                                                      logicOperator: newOperator,
                                                  })),
                                              }
                                    )
                                );
                            }}
                            handleDelete={(index) => removeGroupCondition(absoluteIndex, index)}
                        >
                            {renderConditionInputs(
                                condition,
                                absoluteIndex,
                                (updated) => updateGroupCondition(absoluteIndex, conditionIndex, updated),
                                conditionIndex
                            )}
                        </QueryCondition>,
                    ];
                });

                return (
                    <React.Fragment key={absoluteIndex}>
                        {showConnector && (
                            <Box columnLayout className={style.groupConditionContainer}>
                                <Button
                                    variant="Tertiary"
                                    type="button"
                                    className={style.conditionButton}
                                    onClick={() => updateGroupEntryLogic(absoluteIndex)}
                                    icon={<Chevron color={theme.iconFillColor} className="" />}
                                >
                                    {entry.entryLogicOperator === "OR" ? t("Common.or") : t("Common.and")}
                                </Button>
                            </Box>
                        )}
                        <Box columnLayout className={style.groupContainer}>
                            <QueryConditionGroup
                                conditions={groupConditionNodes}
                                firstGroup={groupRelativeIndex === 0 && topLevelConditions.length === 0}
                                depth={1}
                                logicalOperator={entry.logicOperator ?? FilterLogicOperator.AND}
                                groupHeadingText=""
                                className={style.conditionGroupTable}
                                headerButtons={
                                    <Box className={style.groupButtonContainer}>
                                        <span className={style.groupConditionTitle}>
                                            {entry.conditions[1]?.logicOperator === "OR"
                                                ? t("AdvancedSearch.groupConditionTitleOr")
                                                : t("AdvancedSearch.groupConditionTitleAnd")}
                                        </span>
                                        <IconButton
                                            type="button"
                                            onClick={() => addConditionToGroup(absoluteIndex)}
                                            testId={
                                                testIds.workArea.asset.manageAssetView.filterGroup.addConditionButton
                                            }
                                        >
                                            <AddOutline color={resolveColor("--theme-active-primary")} />
                                        </IconButton>
                                        <IconButton
                                            type="button"
                                            onClick={() => removeEntry(absoluteIndex)}
                                            testId={
                                                testIds.workArea.asset.manageAssetView.filterGroup.removeGroupButton
                                            }
                                        >
                                            <Delete color={resolveColor("--theme-error-primary")} />
                                        </IconButton>
                                    </Box>
                                }
                            />
                        </Box>
                    </React.Fragment>
                );
            })}
        </QueryBuilder>
    );
};
